import type { EmployeeDto, AdminUpdateEmployeeDto} from 'src/api/api_data';

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  Box,
  Card,
  Grid,
  Stack,
  Alert,
  Paper,
  Button,
  Dialog,
  Divider,
  TextField,
  Container,
  Typography,
  AlertTitle,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  DialogContentText,
} from '@mui/material';

import { Label } from 'src/components/label';

import { useRouter } from 'src/routes/hooks';

import { updateEmployee, EmployeeStatus, getEmployeeById, deleteUser, resetDeviceRegistration } from 'src/api/api_data';

import { Iconify } from 'src/components/iconify';

// Kullanıcı durumları için mapping
const STATUS_MAPPING = {
  [EmployeeStatus.ACTIVE]: { label: 'Aktif', color: 'success' },
  [EmployeeStatus.INACTIVE]: { label: 'Pasif', color: 'error' },
  [EmployeeStatus.ON_LEAVE]: { label: 'İzinde', color: 'warning' },
  [EmployeeStatus.BANNED]: { label: 'Yasaklı', color: 'error' },
  [EmployeeStatus.REQUIRES_VERIFICATION]: { label: 'Doğrulama Bekliyor', color: 'warning' }
} as const;

export default function EmployeeEditView() {
  const { userId } = useParams();
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [saving, setSaving] = useState(false); // FE-9: double-submit kilidi
  const [formData, setFormData] = useState<EmployeeDto>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    position: '',
    status: EmployeeStatus.ACTIVE,
    branchId: '',
    branchName: '',
    deviceId: null,
    key: null,
    lastCheckInTime: null,
    totalWorkHours: 0,
    attendanceCount: 0,
    verified: false,
    deleted: false
  });

  useEffect(() => {
    const fetchEmployee = async () => {
      if (!userId) {
        setError('Çalışan ID bulunamadı');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getEmployeeById(userId);
        setFormData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching employee:', err);
        setError('Çalışan bilgileri alınırken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [userId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    if (saving) return; // FE-9: çift-submit kilidi
    // FE-9: form validasyonu (boş ad/soyad + e-posta formatı) — geçersiz veri backend'e gitmesin
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Ad ve soyad zorunludur.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email.trim())) {
      setError('Geçerli bir e-posta adresi girin.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      // Only send fields that should be updated by the admin
      // Don't send verified and deleted as they are managed by separate actions
      const updatePayload: AdminUpdateEmployeeDto = {
        id: formData.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        position: formData.position,
        branchId: formData.branchId,
        branchName: formData.branchName
      };

      await updateEmployee(userId!, updatePayload);
      router.back();
    } catch (submitError) {
      console.error('Error updating employee:', submitError);
      setError('Kullanıcı güncellenirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // Future feature: Delete Key
  // const handleDeleteKey = async () => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     const tokenValidation = validateToken(token);
  //     if (!tokenValidation.isValid) {
  //       setError(`Oturum hatası: ${tokenValidation.error}. Lütfen tekrar giriş yapın.`);
  //       localStorage.removeItem('token');
  //       navigate('/sign-in');
  //       return;
  //     }
  //     if (userId) {
  //       await deleteEmployeeKey(userId);
  //       setError('Anahtar başarıyla silindi');
  //       setTimeout(() => setError(null), 2000);
  //     } else {
  //       setError('Kullanıcı ID bulunamadı');
  //     }
  //   } catch (deleteError) {
  //     setError('Anahtar silinirken bir hata oluştu');
  //   }
  // };

  const handleResetDevice = () => {
    setResetConfirmOpen(true);
  };

  const handleConfirmResetDevice = async () => {
    try {
      setResetLoading(true);
      await resetDeviceRegistration(userId!);
      
      // Refresh employee data to reflect the device reset
      const refreshedData = await getEmployeeById(userId!);
      setFormData(refreshedData);
      
      setError(`Cihaz başarıyla sıfırlandı. ${formData.firstName} ${formData.lastName} artık yeni bir cihaz kaydedebilir.`);
      setTimeout(() => {
        setError(null);
      }, 5000);
      setResetConfirmOpen(false);
    } catch (resetError) {
      console.error('Device reset failed:', resetError);
      setError('Cihaz sıfırlanırken bir hata oluştu.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCancelResetDevice = () => {
    setResetConfirmOpen(false);
  };

  const handleDeleteEmployee = async () => {
    const confirmed = window.confirm(
      `${formData.firstName} ${formData.lastName} adlı çalışanı kalıcı olarak silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz!`
    );

    if (confirmed) {
      try {
        await deleteUser(userId!);
        router.back();
      } catch (deleteError) {
        console.error('Error deleting employee:', deleteError);
        setError('Çalışan silinirken bir hata oluştu');
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Error state when employee not found
  if (error && !formData.id) {
    return (
      <Container maxWidth="lg">
        <Box mt={5}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => router.back()}
          >
            Çalışan Listesine Dön
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
        <Box mb={5}>
          <Typography variant="h4">Çalışan Düzenle</Typography>
        </Box>

        {error && (
          <Alert
            severity={error.includes('başarıyla') ? 'success' : 'error'}
            variant="filled"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            <AlertTitle>
              {error.includes('başarıyla') ? 'Başarılı' : 'Hata'}
            </AlertTitle>
            {error}
          </Alert>
        )}

        <Card>
          <Stack spacing={4} sx={{ p: 3 }}>
            {/* Kişisel Bilgiler */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom>
                Kişisel Bilgiler
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ad"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Soyad"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="E-posta"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>
              </Grid>
            </Paper>

            <Divider />

            {/* Çalışma Bilgileri */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom>
                Çalışma Bilgileri
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Pozisyon"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Durum"
                    value={STATUS_MAPPING[formData.status]?.label ?? formData.status}
                    disabled
                    helperText="Durum bu formdan değiştirilmez; onay/yasakla işlemlerini kullanın."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Şube"
                    name="branchName"
                    value={formData.branchName}
                    onChange={handleChange}
                    disabled
                  />
                </Grid>
              </Grid>
            </Paper>

            <Divider />

            {/* Cihaz Durumu */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom>
                Cihaz Durumu
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Doğrulama Durumu
                    </Typography>
                    <Label color={formData.verified ? 'success' : 'error'}>
                      {formData.verified ? 'Doğrulanmış ' : 'Doğrulanmamış '}
                    </Label>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      Backend tarafından yönetilir
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cihaz ID"
                    value={formData.deviceId || 'Kayıtlı cihaz yok'}
                    disabled
                    sx={{
                      '& .MuiInputBase-input': {
                        color: formData.deviceId ? 'text.primary' : 'text.disabled',
                        fontWeight: formData.deviceId ? 'normal' : 'italic'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Divider />

            {/* İşlem Butonları */}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleResetDevice}
                  disabled={resetLoading}
                  startIcon={<Iconify icon="solar:refresh-bold" />}
                >
                  Cihazı Sıfırla
                </Button>
                {/* Future Feature: Delete Key
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteKey}
                  disabled={!formData.verified}
                >
                  Anahtarı Sil
                </Button>
                */}
              </Stack>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => router.back()}>
                  İptal
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteEmployee}
                  startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                >
                  Çalışanı Sil
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Kaydediliyor…' : 'Kaydet'}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Card>

        {/* Reset Device Confirmation Dialog */}
        <Dialog
          open={resetConfirmOpen}
          onClose={handleCancelResetDevice}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Cihaz Kaydını Sıfırla?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <strong>{formData.firstName} {formData.lastName}</strong> adlı çalışanın mevcut cihaz kaydı silinecektir.
              Çalışan mobil uygulamadan tekrar kayıt olmalıdır.
              <br /><br />
              <strong>Bu işlem geri alınamaz.</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCancelResetDevice}
              disabled={resetLoading}
            >
              İptal
            </Button>
            <Button
              onClick={handleConfirmResetDevice}
              color="warning"
              variant="contained"
              disabled={resetLoading}
              startIcon={resetLoading ? <CircularProgress size={20} /> : <Iconify icon="solar:refresh-bold" />}
            >
              {resetLoading ? 'Sıfırlanıyor...' : 'Cihazı Sıfırla'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
}